import { createFileRoute, Outlet, Link, useRouterState, useNavigate } from '@tanstack/react-router';
import { useAuthStore } from '@wordpuppi/shared';
import { useEffect, useRef } from 'react';
import { AccountMenu } from '@/components/AccountMenu';
import { ThemeToggle } from '@/components/ThemeToggle';
import logoBlack from '@/assets/logo-black-300w.webp';
import logoWhite from '@/assets/logo-white-300w.webp';

export const Route = createFileRoute('/sites')({
  component: SitesLayout,
});

function SitesLayout() {
  const { isAuthenticated } = useAuthStore();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (!isAuthenticated && !hasRedirected.current) {
      hasRedirected.current = true;
      navigate({ to: '/login' });
    }
  }, [isAuthenticated, navigate]);

  if (!isAuthenticated) {
    return null; // Show nothing while redirecting
  }

  // Check if we're in a site-specific route (which has its own layout)
  const isInSiteRoute = /^\/sites\/[^/]+/.test(currentPath) && currentPath !== '/sites/new';

  // For site-specific routes, just render the outlet (sidebar handled by sites.$siteSlug.tsx)
  if (isInSiteRoute) {
    return <Outlet />;
  }

  // For /sites and /sites/new, render with a simple header
  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-card text-card-foreground py-3 px-4 sm:px-8 border-b border-border">
        <div className="max-w-5xl mx-auto flex justify-between items-center gap-3">
          <Link
            to="/sites"
            className="no-underline block rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            <img src={logoBlack} alt="WordPuppi" className="dark:hidden" style={{ height: 'calc(var(--spacing) * 14)' }} />
            <img src={logoWhite} alt="WordPuppi" className="hidden dark:block" style={{ height: 'calc(var(--spacing) * 14)' }} />
          </Link>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <AccountMenu />
          </div>
        </div>
      </header>
      <main className="flex-1 p-4 sm:p-8 max-w-5xl mx-auto w-full">
        <Outlet />
      </main>
    </div>
  );
}
